import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import {
	json,
	type MetaFunction,
	type LoaderFunctionArgs,
} from '@remix-run/node'
import {
	Link,
	Outlet,
	useFetcher,
	useLoaderData,
	useLocation,
	useNavigate,
} from '@remix-run/react'
import { formatDistanceToNow } from 'date-fns'
import { Fragment, useState } from 'react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { NoProductExists } from '#app/components/error-components.tsx'
import { ProductTitle } from '#app/components/product-title.tsx'
import { ReportProductForm } from '#app/components/report-product-form.tsx'
import {
	ADD_REVIEW_ENDPOINT,
	Review,
	ReviewProvider,
} from '#app/components/review.js'
import { Button } from '#app/components/ui/button.js'
import { Icon } from '#app/components/ui/icon.js'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import {
	SITE_BRAND_NAME,
	SITE_FULL_TITLE,
} from '#app/utils/client-constants.js'
import { prisma } from '#app/utils/db.server.ts'
import { getProductImgSrc } from '#app/utils/misc.tsx'
import {
	MAX_META_DESCRIPTION_LENGTH,
	MAX_META_FIRST_LINE_DESCRIPTION_LENGTH,
} from '#app/utils/providers/constants.ts'
import { useOptionalUser } from '#app/utils/user.js'

import {
	OwnedUpdateSchema,
	type action as ownedAction,
} from './$categorySlug.$productId.$productSlug.owned'
import {
	WishlistUpdateSchema,
	type action as wishlistAction,
} from './$categorySlug.$productId.$productSlug.wishlist'
import defaultProductImage from '/img/image.png'

export async function loader({ params }: LoaderFunctionArgs) {
	const product = await prisma.product.findUnique({
		where: { id: params.productId },
		select: {
			id: true,
			name: true,
			description: true,
			createdAt: true,
			year: true,
			user: { select: { id: true } },
			category: { select: { name: true } },
			addedByUserId: true,
			images: {
				select: {
					id: true,
					altText: true,
				},
			},
			wishlistedBy: { select: { userId: true } },
			ownedBy: { select: { userId: true } },
			reviews: {
				select: {
					id: true,
					rating: true,
					content: true,
					createdAt: true,
					updatedAt: true,
					likes: { select: { id: true, type: true, userId: true } },
					user: {
						select: {
							id: true,
							username: true,
							name: true,
							image: { select: { id: true } },
							_count: { select: { reviews: true, addedProducts: true } },
						},
					},
					comments: {
						select: {
							id: true,
							content: true,
							reviewId: true,
							createdAt: true,
							updatedAt: true,
							likes: { select: { id: true, type: true, userId: true } },
							user: {
								select: {
									id: true,
									username: true,
									name: true,
									image: { select: { id: true } },
									_count: { select: { reviews: true, addedProducts: true } },
								},
							},
						},
					},
				},
			},
		},
	})

	invariantResponse(product, 'Not found', { status: 404 })

	const date = new Date(product.createdAt)
	const timeAgo = formatDistanceToNow(date)

	return json({
		product,
		timeAgo,
	})
}

export default function ProductRoute() {
	const data = useLoaderData<typeof loader>()
	const location = useLocation()
	const navigate = useNavigate()
	const user = useOptionalUser()

	const reviewsCount = data.product.reviews.length
	const reviewsWithContentCount = data.product.reviews.filter(
		review => review.content,
	).length
	const totalRating = data.product.reviews.reduce(
		(acc, review) => acc + review.rating,
		0,
	)
	const averageRating = parseFloat(
		(totalRating / (reviewsCount || 1)).toFixed(2),
	)
	const [currentImage, setCurrentImage] = useState(0)
	const sortedReviews = data.product.reviews.sort(
		(a, b) => b.likes.length - a.likes.length,
	)
	const userReview =
		user &&
		sortedReviews.find(review => review.user && review.user.id === user.id)
	const otherReviews = sortedReviews.filter(
		review => !userReview || review.id !== userReview.id,
	)
	const wishlistFetcher = useFetcher<typeof wishlistAction>()
	const [wishlistForm, wishlistFields] = useForm({
		id: 'update-wishlist',
		constraint: getZodConstraint(WishlistUpdateSchema),
		lastResult: wishlistFetcher.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: WishlistUpdateSchema })
		},
		shouldRevalidate: 'onBlur',
	})
	const wishlistUpdateAction = data.product.wishlistedBy.find(
		i => user && i.userId === user.id,
	)
		? 'remove'
		: 'add'

	const ownedFetcher = useFetcher<typeof ownedAction>()
	const [ownedForm, ownedFields] = useForm({
		id: 'update-owned',
		constraint: getZodConstraint(OwnedUpdateSchema),
		lastResult: ownedFetcher.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: OwnedUpdateSchema })
		},
		shouldRevalidate: 'onBlur',
	})
	const ownedUpdateAction = data.product.ownedBy.find(
		i => user && i.userId === user.id,
	)
		? 'remove'
		: 'add'

	const isSubmittingOwned = ownedFetcher.state === 'submitting'
	const isSubmittingWishlist = wishlistFetcher.state === 'submitting'
	const isLoading = isSubmittingOwned || isSubmittingWishlist
	const imageSrc = data.product.images?.[currentImage]?.id
		? getProductImgSrc(data.product.images[currentImage].id)
		: defaultProductImage

	return (
		<div className="mx-2 flex max-w-6xl flex-col gap-8">
			{location.pathname.endsWith('edit-product') ? (
				<Outlet />
			) : (
				<div className="flex flex-col">
					<div className="flex w-full flex-col">
						<div className="flex justify-between">
							<div className="-mt-4 flex">
								<Button
									variant={'link'}
									className="h-fit w-fit items-start"
									onClick={() => {
										navigate('/rankings')
									}}
								>
									<Icon name="arrow-left" size="sm">
										<span className="text-sm">Back to Rankings</span>
									</Icon>
								</Button>
								{user && data.product.addedByUserId === user.id ? (
									<Button
										variant={'link'}
										asChild
										className="h-fit w-fit items-start"
									>
										<Link
											to="edit-product"
											prefetch="intent"
											className="flex gap-1"
										>
											<Icon name="pencil-2" size="sm">
												<span className="text-sm">Edit</span>
											</Icon>
										</Link>
									</Button>
								) : null}
							</div>
							<div className="flex items-center gap-1 max-md:hidden">
								<span className="text-xs text-muted-foreground">
									{data.timeAgo}
								</span>
								<ReportProductForm productId={data.product.id} />
							</div>
						</div>
						<ProductTitle
							product={data.product}
							averageRating={averageRating}
							reviewsCount={reviewsCount}
							reviewsWithContentCount={reviewsWithContentCount}
							className="px-1 py-4 md:hidden"
						/>
						<div className="md:flex md:flex-row md:gap-4">
							<div className="w-full md:w-1/2" aria-label="Product images">
								<div className="relative mb-1 h-96">
									<a href={imageSrc}>
										<img
											className="h-full w-full rounded-xl object-cover"
											src={imageSrc}
											alt={
												data.product.images?.[currentImage]?.altText ??
												'default product image'
											}
										/>
									</a>
								</div>
								<div className="flex gap-2 overflow-x-auto pb-3 pl-1 pt-1">
									{data.product.images.slice(0, 5).map((image, index) => (
										<Fragment key={image.id}>
											<img
												className={`aspect-square h-24 w-24 cursor-pointer rounded-md object-cover hover:opacity-80 ${index === currentImage ? 'outline outline-4 outline-gray-400' : ''}`}
												src={
													image.id
														? getProductImgSrc(image.id)
														: defaultProductImage
												}
												alt={image.altText ?? 'default product image'}
												onClick={() => setCurrentImage(index)}
											/>
										</Fragment>
									))}
								</div>
							</div>
							<div className="flex w-full flex-col gap-1 py-4 md:w-1/2 md:gap-8">
								<ProductTitle
									product={data.product}
									averageRating={averageRating}
									reviewsCount={reviewsCount}
									reviewsWithContentCount={reviewsWithContentCount}
									className="max-md:hidden"
								/>
								<div className="flex w-full flex-col">
									<div className="flex flex-col gap-2 md:gap-4">
										<div className="flex flex-col gap-4">
											{userReview ? (
												<Button
													variant={'secondary'}
													disabled
													className="hover:bg-inherit max-sm:text-sm"
													size={'lg'}
												>
													<Icon name="check">Review Added</Icon>
												</Button>
											) : (
												<Button variant={'default'} size={'lg'} asChild>
													<Link
														to={ADD_REVIEW_ENDPOINT}
														prefetch="intent"
														preventScrollReset={true}
														className="flex items-center gap-1 max-sm:text-sm"
													>
														<Icon name="pencil-1">Add a Review</Icon>
													</Link>
												</Button>
											)}
											<div className="grid grid-cols-2 gap-2">
												<wishlistFetcher.Form
													method="POST"
													action="wishlist"
													className="col-span-1"
													preventScrollReset={true}
													{...getFormProps(wishlistForm)}
												>
													<input
														{...getInputProps(
															wishlistFields.wishlistUpdateAction,
															{
																type: 'hidden',
															},
														)}
														value={wishlistUpdateAction}
													/>
													<input
														{...getInputProps(wishlistFields.productId, {
															type: 'hidden',
														})}
														value={data.product.id}
													/>
													<StatusButton
														variant={'secondary'}
														type="submit"
														className="w-full max-sm:px-0"
														status={isLoading ? 'pending' : 'idle'}
													>
														<Icon
															name={`heart-${wishlistUpdateAction === 'add' ? 'empty' : 'filled'}`}
														>
															<span className="max-sm:text-sm">
																{wishlistUpdateAction === 'add'
																	? 'Add'
																	: 'Added'}{' '}
																to Wishlist
															</span>
														</Icon>
													</StatusButton>
												</wishlistFetcher.Form>
												<ownedFetcher.Form
													method="POST"
													action="owned"
													className="col-span-1"
													preventScrollReset={true}
													{...getFormProps(ownedForm)}
												>
													<input
														{...getInputProps(ownedFields.ownedUpdateAction, {
															type: 'hidden',
														})}
														value={ownedUpdateAction}
													/>
													<input
														{...getInputProps(ownedFields.productId, {
															type: 'hidden',
														})}
														value={data.product.id}
													/>
													<StatusButton
														variant={'secondary'}
														type="submit"
														className="w-full max-sm:px-0"
														status={isLoading ? 'pending' : 'idle'}
													>
														<Icon
															name={`backpack-${ownedUpdateAction === 'add' ? 'empty' : 'filled'}`}
															className="max-sm:gap-0"
														>
															<span className="max-sm:text-sm">
																{ownedUpdateAction === 'add' ? 'Add' : 'Added'}{' '}
																to Owned
															</span>
														</Icon>
													</StatusButton>
												</ownedFetcher.Form>
											</div>
										</div>
										<div className="flex items-center gap-1 self-end md:hidden">
											<span className="text-xs text-muted-foreground">
												{data.timeAgo}
											</span>
											<ReportProductForm productId={data.product.id} />
										</div>
										<p className="text-foreground">
											{data.product.description}
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
			<div className="flex flex-col gap-6">
				{!userReview && location.pathname.endsWith(ADD_REVIEW_ENDPOINT) ? (
					<Outlet />
				) : (
					userReview && (
						<ReviewProvider addedByUserId={data.product.addedByUserId}>
							<Review review={userReview}>
								<Review.Comments comments={userReview.comments} />
							</Review>
						</ReviewProvider>
					)
				)}
				{otherReviews.map(review => {
					return (
						<ReviewProvider
							key={review.id}
							addedByUserId={data.product.addedByUserId}
						>
							<Review key={review.id} review={review}>
								<Review.Comments comments={review.comments} />
							</Review>
						</ReviewProvider>
					)
				})}
			</div>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	const name = data?.product.name || "Product doesn't exist"
	const category = data?.product.category.name || ''
	const productDescription = data?.product.description
	const firstReviewContent = data?.product.reviews?.[0]?.content || null
	const truncateText = (text: string, length: number) => {
		return text.length > length ? text.slice(0, length - 3) + '...' : text
	}
	const description = productDescription
		? truncateText(productDescription, MAX_META_FIRST_LINE_DESCRIPTION_LENGTH)
		: firstReviewContent
			? truncateText(firstReviewContent, MAX_META_FIRST_LINE_DESCRIPTION_LENGTH)
			: 'Check out the latest reviews and join the discussion.'
	const finalDescription = truncateText(
		`${description} Is ${name} worth it? See what others think on ${SITE_BRAND_NAME}`,
		MAX_META_DESCRIPTION_LENGTH,
	)

	return [
		{
			title: `${name} ${category} reviews, specs, experience | ${SITE_FULL_TITLE}`,
		},
		{
			name: 'description',
			content: finalDescription,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				403: () => <p>You are not allowed to do that</p>,
				404: ({ params }) => <NoProductExists productId={params.productId} />,
			}}
		/>
	)
}
